import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Flame, Snowflake, Sun } from "lucide-react";
import { IMAGES } from "@/lib/site";
import { Overline } from "./Reveal";

const steps = [
  {
    n: "01",
    title: "Heat",
    subtitle: "Traditional Finnish sauna",
    stat: "110°C target",
    body: "Step into deep, dry heat and let the day dissolve. Ten minutes, twenty — you set the pace.",
    icon: Flame,
    accent: "text-ember",
    cut: IMAGES.expCutHeat,
    // exact placement on the main scene (template-matched)
    pos: { left: "30.87%", top: "8.18%", width: "36.3%" },
    card: { left: "67.5%", top: "13%", width: "27%" },
    line: { left: "66%", top: "19%", width: "1.6%", height: "1px" },
  },
  {
    n: "02",
    title: "Cold",
    subtitle: "Ice bath",
    stat: "3–5°C",
    body: "Lower in, find your breath, stay a beat longer than you thought you could.",
    icon: Snowflake,
    accent: "text-ice",
    cut: IMAGES.expCutCold,
    pos: { left: "8.01%", top: "34.01%", width: "20.45%" },
    card: { left: "2%", top: "96%", width: "22%" },
    line: { left: "16.5%", top: "81%", width: "1px", height: "3%" },
    below: true,
  },
  {
    n: "03",
    title: "Reset",
    subtitle: "Outdoor recovery deck",
    stat: "Open air",
    body: "Towel off in the open air. Sit, stretch, chat — then run the circuit again.",
    icon: Sun,
    accent: "text-ember",
    cut: IMAGES.expCutReset,
    pos: { left: "60.83%", top: "42.31%", width: "34.39%" },
    card: { left: "68%", top: "96%", width: "27%" },
    line: { left: "78%", top: "77%", width: "1px", height: "6.2%" },
    below: true,
  },
];

const RANGES = [
  [0.08, 0.2],
  [0.36, 0.48],
  [0.64, 0.76],
];

const InfoCard = ({ step }) => (
  <div className="border border-line bg-ink/85 backdrop-blur-md px-4 py-3.5 sm:px-5 sm:py-4 text-left">
    <div className="flex items-baseline gap-2.5">
      <span className={`font-display italic text-base ${step.accent}`}>{step.n}</span>
      <h3 className="font-display uppercase tracking-wide text-xl sm:text-2xl text-cream">{step.title}</h3>
      <step.icon className={`h-4 w-4 self-center ${step.accent}`} aria-hidden="true" />
      <span className={`ml-auto font-display text-base sm:text-lg ${step.accent}`}>{step.stat}</span>
    </div>
    <p className="mt-1 text-[9px] sm:text-[10px] tracking-[0.2em] uppercase text-sand/80">{step.subtitle}</p>
    <p className="mt-1.5 text-xs sm:text-sm font-light leading-relaxed text-sand">{step.body}</p>
  </div>
);

const GlowCut = ({ step, progress, range, nextStart }) => {
  const [a, b] = range;
  const nA = nextStart ?? 2;
  const on = useTransform(progress, [a, b, nA - 0.06, nA], [0, 1, 1, 0]);
  return (
    <motion.div style={{ opacity: on, ...step.pos }} className="absolute pointer-events-none">
      <img src={step.cut} alt="" aria-hidden="true" className="w-full h-auto select-none" />
    </motion.div>
  );
};

const Spot = ({ step, progress, range, nextStart }) => {
  const [a, b] = range;
  const nA = nextStart ?? 2;
  const on = useTransform(progress, [a, b, nA - 0.06, nA], [0, 1, 1, 0]);
  const cardY = useTransform(progress, [a, b], [24, 0]);

  return (
    <>
      {/* the individual cutout, glowing, sat exactly on top of the main image */}
      <motion.div
        style={{ opacity: on, ...step.pos }}
        data-testid={`experience-frame-${step.title.toLowerCase()}`}
        className="absolute pointer-events-none"
      >
        <img src={step.cut} alt="" aria-hidden="true" className="w-full h-auto select-none" />
      </motion.div>
      {/* connector from feature to info card */}
      <motion.div
        style={{ opacity: on, ...step.line }}
        className="absolute bg-white/60 pointer-events-none"
        aria-hidden="true"
      />
      {/* info card beside the area — hands over to the next as you scroll */}
      <motion.div
        style={{ opacity: on, y: cardY, ...step.card }}
        data-testid={`experience-info-${step.title.toLowerCase()}`}
        className="absolute z-10"
      >
        <div className={step.below ? "-translate-y-full" : ""}>
          <InfoCard step={step} />
        </div>
      </motion.div>
    </>
  );
};

const MobileCard = ({ step, progress, range }) => {
  const [a, b] = range;
  const on = useTransform(progress, [a, b], [0, 1]);
  const y = useTransform(progress, [a, b], [20, 0]);
  return (
    <motion.div style={{ opacity: on, y }}>
      <InfoCard step={step} />
    </motion.div>
  );
};

const MobileStage = ({ progress }) => {
  const headOpacity = useTransform(progress, [0, 0.06], [1, 0]);
  const headY = useTransform(progress, [0, 0.06], [0, -28]);
  const groupY = useTransform(progress, [0, 0.07], [0, -88]);

  return (
    <div className="md:hidden absolute inset-0 flex flex-col px-5 pt-[88px] overflow-hidden">
      {/* copy up top — fades away as the scene rides to the top */}
      <motion.div style={{ opacity: headOpacity, y: headY }}>
        <Overline>The experience</Overline>
        <h2 className="mt-3 font-display uppercase text-3xl font-normal tracking-wide leading-tight text-cream">
          Heat. Cold. <em className="italic text-ember">Reset.</em>
        </h2>
      </motion.div>

      {/* the scene rides up to the top of the screen, then stops */}
      <motion.div style={{ y: groupY }}>
        <div className="relative w-full aspect-[1672/941] mt-5">
          <img
            src={IMAGES.expScene}
            alt="The TEMPA deck: ice bath, sauna and recovery lounge on black water"
            className="absolute inset-0 h-full w-full brightness-[.55]"
          />
          {/* features stay lit once revealed on mobile */}
          {steps.map((step, i) => (
            <GlowCut key={step.n} step={step} progress={progress} range={RANGES[i]} />
          ))}
        </div>
        <div className="mt-4 space-y-3">
          {steps.map((step, i) => (
            <MobileCard key={step.n} step={step} progress={progress} range={RANGES[i]} />
          ))}
        </div>
      </motion.div>
    </div>
  );
};

const Experience = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  return (
    <section id="experience" ref={ref} data-testid="experience-section" className="relative h-[320vh]">
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-black">
        {/* heading floats over the black sky (desktop) */}
        <div className="hidden md:block absolute top-[88px] sm:top-[100px] left-5 sm:left-10 z-20 max-w-md">
          <Overline>The experience</Overline>
          <h2 className="mt-3 font-display uppercase text-3xl sm:text-4xl lg:text-5xl font-normal tracking-wide leading-tight text-cream">
            Heat. Cold. <em className="italic text-ember">Reset.</em>
          </h2>
          <p className="mt-3 hidden md:block text-sm lg:text-base font-light leading-relaxed text-sand">
            One private circuit — keep scrolling to walk it.
          </p>
        </div>

        {/* ── desktop: the venue fills the entire section ── */}
        <div className="hidden md:block absolute inset-0">
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 aspect-[1672/941]"
            style={{ width: "min(100vw, 177.8vh)" }}
          >
            <img
              src={IMAGES.expScene}
              alt="The TEMPA deck: ice bath, sauna and recovery lounge on black water"
              className="absolute inset-0 h-full w-full brightness-[.55] select-none"
            />
            {steps.map((step, i) => (
              <Spot key={step.n} step={step} progress={scrollYProgress} range={RANGES[i]} nextStart={RANGES[i + 1]?.[0]} />
            ))}
          </div>
        </div>

        {/* ── mobile: copy → scene rides to top → cards swap while features stay lit ── */}
        <MobileStage progress={scrollYProgress} />
      </div>
    </section>
  );
};

export default Experience;
